// SPDX-License-Identifier: AGPL-3.0-or-later
// Copyright (C) 2026 Ben Richardson — https://benrichardson.dev
// See ADDITIONAL-TERMS.md for the section 7(b) attribution requirement.
//
// Skyhold — the DOM screens: menu, help, about, and the results panel. Pure HTML builders (no
// state), wired up by main.ts.
//
// The results panel is written to the rule that every player's outcome shows, every time — a
// per-player breakdown of what each side actually did, not a name and a verdict. In an abstract the
// honest columns are peak height, turns spent up high, blocks and domes placed, and MOBILITY (how
// many legal turns you averaged), which is the number that makes "I got squeezed" visible.

import { escapeHtml, plural } from './dom';
import { MODE_IDS, modeOf, type Mode } from './modes';
import { POWERS, powerOf } from './rules';
import { SEAT_NAME } from './palette';
import { TIERS } from './bot';
import type { ResultRow } from './duel';
import type { Verdict } from './analysis';

export const ATTRIB =
  'Built by <a href="https://benrichardson.dev/" target="_blank" rel="noopener">benrichardson.dev</a> · ' +
  '<a href="https://lab.benrichardson.dev" target="_blank" rel="noopener">more games, tools &amp; sites</a>';

function modeChips(modeId: string): string {
  return MODE_IDS.map(
    (id) =>
      `<button class="chip${id === modeId ? ' on' : ''}" data-mode="${id}" aria-pressed="${id === modeId}">${escapeHtml(modeOf(id).name)}</button>`,
  ).join('');
}

function tierChips(tierId: string): string {
  return TIERS.map(
    (t) =>
      `<button class="chip${t.id === tierId ? ' on' : ''}" data-tier="${t.id}" aria-pressed="${t.id === tierId}">${escapeHtml(t.name)}</button>`,
  ).join('');
}

export function menuHtml(o: { modeId: string; tierId: string; record: [number, number]; muted: boolean }): string {
  const mode = modeOf(o.modeId);
  const tier = TIERS.find((t) => t.id === o.tierId) ?? TIERS[0];
  const [w, l] = o.record;
  return `
    <section class="screen menu">
      <div class="brand">
        <div class="logo" aria-hidden="true"><i></i><i></i><i></i></div>
        <h1>Skyhold</h1>
        <p class="tagline">Move one builder, raise one block — first to stand three storeys high wins the sky.</p>
      </div>
      <div class="modepick">
        <div class="chips" role="group" aria-label="Choose a mode">${modeChips(o.modeId)}</div>
        <p class="mode-blurb">${escapeHtml(mode.blurb)}</p>
      </div>
      <div class="modepick">
        <div class="chips" role="group" aria-label="Choose an opponent">${tierChips(o.tierId)}</div>
        <p class="mode-blurb">${escapeHtml(tier.blurb)}</p>
        <p class="mode-best">${w + l === 0 ? 'No games yet in this mode' : `${plural(w, 'win')} · ${l} lost`}</p>
      </div>
      <div class="menu-actions">
        <button class="btn primary" id="playSolo">Play</button>
        <button class="btn" id="playFriends">Play with a friend</button>
      </div>
      <div class="menu-links">
        <button class="linkbtn" id="help">How to play</button>
        <button class="linkbtn" id="about">About</button>
        <button class="linkbtn" id="mute">${o.muted ? 'Sound: off' : 'Sound: on'}</button>
      </div>
    </section>`;
}

export function helpHtml(): string {
  return `
    <h2>How to play</h2>
    <button class="sheet-close x" aria-label="Close">✕</button>
    <ul class="help-list">
      <li><b>A turn is two halves.</b> Move one of your builders one square in any direction, then raise one block on any empty square beside it.</li>
      <li><b>Climb one, drop any.</b> You can step up at most one storey at a time, but you can drop as far as you like.</li>
      <li><b>Step onto a third storey and you win the sky.</b> You also win if your opponent is left with no legal move.</li>
      <li><b>A fourth block is a dome.</b> Nobody ever enters a dome — so a block is a staircase for you and a wall for them at the same time.</li>
      <li><b>The opening.</b> You each place your builders one at a time on a board that already has some stonework on it. Whoever places first moves second.</li>
    </ul>
    <p class="help-note">Tap a builder, tap where it goes, then tap where to build. <b>Undo</b> stays available until you have finished the turn.</p>
    <h3>The power tiles (Charter)</h3>
    <ul class="help-list small">
      ${POWERS.map((p) => `<li><b>${escapeHtml(p.name)}</b> — ${escapeHtml(p.text)}</li>`).join('')}
    </ul>`;
}

export function aboutHtml(): string {
  return `
    <h2>About Skyhold</h2>
    <button class="sheet-close x" aria-label="Close">✕</button>
    <p>A two-player game of building and climbing on a small grid: every block you place is a staircase for you and a wall for your opponent, and the win comes from making two threats they can only answer one of.</p>
    <p>Play it alone against a searching opponent, or open a room and send a friend the link — that game runs <b>peer to peer</b>, straight between the two browsers. There is no game server and no account. A public relay is used only to introduce the two browsers to each other; no game state ever reaches it and nothing is stored.</p>
    <p>No cookies, no fingerprinting, no third-party fonts. Anonymous, cookie-less page-view counts via Cloudflare Web Analytics.</p>
    <p class="about-attrib">${ATTRIB}</p>`;
}

function resultRow(r: ResultRow, mode: Mode): string {
  const tile = mode.powers ? powerOf(r.power) : null;
  const who = `${escapeHtml(r.name)}${r.isSelf ? ' (you)' : ''}`;
  return `
    <div class="rrow ${r.won ? 'won' : ''} seat${r.seat}">
      <div class="rname"><span class="rtok p${r.seat + 1}"></span>${who}</div>
      <div class="rverdict">${r.won ? 'Won' : r.left ? 'Left' : 'Lost'}</div>
      <dl class="rstats">
        <div><dt>Turns</dt><dd>${r.turns}</dd></div>
        <div><dt>Peak height</dt><dd>${r.peak}</dd></div>
        <div><dt>Turns up high</dt><dd>${r.highTurns}</dd></div>
        <div><dt>Blocks</dt><dd>${r.blocks}</dd></div>
        <div><dt>Domes</dt><dd>${r.domes}</dd></div>
        <div><dt title="Average number of legal turns available — how boxed in this player was">Choices / turn</dt><dd>${r.mobility.toFixed(0)}</dd></div>
        ${tile ? `<div><dt>${escapeHtml(tile.name)}</dt><dd>${r.charges} spent</dd></div>` : ''}
      </dl>
    </div>`;
}

export function resultsHtml(o: {
  rows: ResultRow[];
  mode: Mode;
  reason: string;
  winner: number;
  tally: [number, number];
  round: number;
  fault: string | null;
}): string {
  const win = o.rows.find((r) => r.seat === o.winner);
  let head: string;
  let sub: string;
  if (o.fault === 'build') {
    head = 'Round abandoned';
    sub = 'Your opponent is running a different build of Skyhold. Reload the page and start again.';
  } else if (o.fault === 'desync') {
    head = 'Round abandoned';
    sub = 'The two boards stopped agreeing, so the round was stopped rather than played out wrongly.';
  } else if (o.reason === 'nocontest') {
    head = 'No contest';
    sub = 'The round ended before a move was made, so nothing was scored.';
  } else if (!win) {
    head = 'Round over';
    sub = '';
  } else {
    head = `${escapeHtml(win.name)} holds the sky`;
    sub =
      o.reason === 'climb'
        ? `Climbed to the third storey on turn ${win.turns}.`
        : o.reason === 'walkover'
          ? 'Their opponent left the room.'
          : `${escapeHtml(o.rows.find((r) => r.seat !== o.winner)?.name ?? 'The loser')} was sealed in — no legal turn left.`;
  }

  return `
    <div class="results-head">
      <h2>${head}</h2>
      <p class="results-sub">${sub}</p>
      <p class="results-tally">Match: ${escapeHtml(o.rows[0].name)} ${o.tally[0]} — ${o.tally[1]} ${escapeHtml(o.rows[1].name)} · ${escapeHtml(o.mode.name)} · round ${o.round}</p>
    </div>
    <canvas class="iso" id="isoBoard" width="560" height="380" aria-label="The final board"></canvas>
    <div class="rrows">${o.rows.map((r) => resultRow(r, o.mode)).join('')}</div>
    <div class="verdict" id="verdict"></div>`;
}

export function verdictHtml(v: Verdict | null): string {
  if (!v) return '';
  if (!v.done) return `<p class="verdict-progress">Looking back over the game… ${v.progress} / ${v.total}</p>`;
  return `<p class="verdict-line">${escapeHtml(v.summary)}</p>`;
}

export const seatLabel = (seat: number): string => SEAT_NAME[seat] ?? `Player ${seat + 1}`;
